// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import { Tracking } from 'web-core/src/tracking';
import { getUtmParams } from 'client/actions/preRegistration';
import type { RouterType } from 'types/react-router.flow';
import LocationPage from './locationPage';
import VisionLandingPage from './visionLandingPage';
import '../styles/onboarding.scss';

type Props = {
  contentfulData: {
    disabledStates: Array<string>,
    isDisabled: boolean,
  },
  enableDentalVisionEmailCollection: boolean,
  getAddressFromZip: Function,
  members: Array<Object>,
  router: RouterType,
  saveAddressToApp: Function,
  subscribe: Function,
  user: Object,
  visionApp: {
    app_id: string,
  },
};

type State = {
  zipcode: string,
  zipError: Object | null,
  landing: boolean,
};

const getAddress = visionApp => _.get(visionApp, 'address', {});

const getPrimaryMemberId = (user) => {
  if (!user) {
    return null;
  }
  return _.get(user, 'primaryMemberId');
};

class ZipcodeLookupPage extends Component<Props, State> {
  invalidZip: string;

  static defaultProps = {
    members: [],
    user: null,
  };

  constructor(props: Props) {
    super(props);

    const { visionApp, user, members } = props;
    let address = getAddress(visionApp);
    const primaryMemberId = getPrimaryMemberId(user);

    // If the app doesn't have an address, get the primary member's address
    if (_.isEmpty(address)) {
      const primaryMember = _.find(members, { id: primaryMemberId });
      address = _.get(primaryMember, 'address', {});
    }

    // Check to show either vision landing page or onboarding location page
    const path = _.get(props, 'route.path');
    let landing = false;
    if (path === '/') {
      landing = true;
      Tracking.track('Landing Page Viewed', { Page: 'Vision' });
    }

    if (this.props.enableDentalVisionEmailCollection) {
      Tracking.track('EC Shopping: Email Collection Presented', { display_type: 'vision_zip' });
    }

    this.invalidZip = _.get(props, 'location.query.invalidZip');

    this.state = {
      landing,
      zipcode: this.invalidZip || _.get(address, 'zipcode', ''),
      zipError: this.invalidZip ? { path: 'common.error.invalidZip' } : null,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.enableDentalVisionEmailCollection &&
      this.props.enableDentalVisionEmailCollection
    ) {
      Tracking.track('EC Shopping: Email Collection Presented', { display_type: 'vision_zip' });
    }
  }

  checkAddress(zipcode: string) {
    const {
      contentfulData: { isDisabled, disabledStates },
      router,
    } = this.props;
    this.setState({ zipError: null });
    return this.props.getAddressFromZip(zipcode).then((address) => {
      if (address && address.length === 1) {
        const newAddress = address[0];
        let updatedAddress = newAddress;

        const { visionApp, user } = this.props;
        const existingAddress = getAddress(visionApp);
        const primaryMemberId = getPrimaryMemberId(user);

        // Save address and then go to members page
        if (existingAddress) {
          updatedAddress = { ...existingAddress, ...newAddress };
        }

        const payload = {
          address: updatedAddress,
          app_ids: [this.props.visionApp.app_id],
        };

        return this.props
          .saveAddressToApp(payload, primaryMemberId ? { member_id: primaryMemberId } : null)
          .then(() => {
            if (isDisabled && disabledStates.includes(_.head(address).state)) {
              return router.push('/onboarding/unsupported');
            }
            if (this.invalidZip) {
              return router.replace('/onboarding/applicants');
            }
            return router.push('/onboarding/applicants');
          });
      } else if (address && address.length > 1) {
        if (isDisabled && disabledStates.includes(_.head(address).state)) {
          return router.push('/onboarding/unsupported');
        }
        // Go to county page
        if (this.invalidZip) {
          return router.replace('/onboarding/county');
        }
        return router.push('/onboarding/county');
      }
      // error - invalid zip
      if (this.state.landing) {
        return router.push(`/onboarding/location?invalidZip=${zipcode}`);
      }
      return this.setState({ zipError: { path: 'common.error.invalidZip' }, landing: false });
    });
  }

  async createAccountFromEmail(email: string) {
    if (email) {
      Tracking.track('EC Shopping: Email Collection', {
        location: 'vision',
        display_type: 'email_inline',
        email_submitted: email,
      });
      const utmParams = getUtmParams();
      await this.props.subscribe({
        email,
        signupSource: 'vision_zipcode_page',
        consentIds: ['email_marketing'],
        ...utmParams,
      });
    }
  }

  render() {
    return this.state.landing ? (
      <VisionLandingPage
        submitHandler={(value) => {
          this.setState({ zipcode: value || '' });
          this.checkAddress(value);
        }} />
    ) : (
      <LocationPage
        zipcode={this.state.zipcode || ''}
        zipError={this.state.zipError}
        enableDentalVisionEmailCollection={this.props.enableDentalVisionEmailCollection}
        submitHandler={async (value, email) => {
          await this.createAccountFromEmail(email);
          this.checkAddress(value);
        }} />
    );
  }
}

export default ZipcodeLookupPage;
